import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Boxes, PencilRuler, HardHat, Wallet, ShieldCheck, Network, Sparkles, ArrowRight } from "lucide-react";
import { MarketingLayout, Section } from "@/components/marketing/MarketingLayout";
import { IMG } from "@/lib/constants";
import { Button } from "@/components/ui/button";

const MODULES = [
  { icon: Boxes, t: "PLAN", d: "Site analysis, feasibility, planning", img: IMG.blueprint, to: "/register" },
  { icon: PencilRuler, t: "DESIGN", d: "Floor plans, 3D, elevation, interiors, drawings", img: IMG.villa, to: "/studio" },
  { icon: HardHat, t: "BUILD", d: "Project management, tasks, progress", img: IMG.workersGroup, to: "/register" },
  { icon: Wallet, t: "MANAGE", d: "BOQ, budget, procurement, vendors", img: IMG.materials, to: "/register" },
  { icon: ShieldCheck, t: "INSPECT", d: "Quality, inspections, snagging", img: IMG.highrise, to: "/register" },
  { icon: Network, t: "CONNECT", d: "Clients, contractors, architects, engineers, suppliers", img: IMG.siteSunset, to: "/market" },
  { icon: Sparkles, t: "INTELLIGENCE", d: "AI, predictions, recommendations and insights", img: IMG.cranesSkyline, to: "/intelligence" },
];

export default function Platform() {
  const nav = useNavigate();
  return (
    <MarketingLayout>
      <section className="bg-sp-navy text-white sp-blueprint">
        <div className="sp-container py-24 lg:py-32">
          <div className="sp-eyebrow mb-4">The Platform</div>
          <h1 className="text-4xl lg:text-6xl font-bold tracking-tight leading-tight max-w-3xl">
            One platform.<br /><span className="text-sp-orange">Every</span> construction project.
          </h1>
          <p className="mt-6 text-lg text-white/65 max-w-xl">Seven connected modules, one source of truth — the Project.</p>
        </div>
      </section>

      <Section>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {MODULES.map((m, i) => (
            <motion.button key={m.t} onClick={() => nav(m.to)} data-testid={`platform-card-${m.t.toLowerCase()}`}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="group text-left rounded-xl overflow-hidden border border-slate-200 sp-hover-card bg-white">
              <div className="h-40 overflow-hidden relative">
                <img src={m.img} alt={m.t} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-sp-navy/40" />
                <div className="absolute top-3 left-3 h-9 w-9 rounded-lg bg-sp-orange flex items-center justify-center">
                  <m.icon className="h-4.5 w-4.5 text-white" size={18} />
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <span className="font-bold tracking-wide text-sp-navy">{m.t}</span>
                  <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-sp-orange transition-colors" />
                </div>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{m.d}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </Section>

      <section className="bg-sp-light">
        <div className="sp-container py-16 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-sp-navy">Everything attaches to the Project.</h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">Chat, documents, drawings, BOQ, budget, payments, materials, tasks, timeline, quality, people and AI context — one Project ID connects it all.</p>
          <Button data-testid="platform-cta" onClick={() => nav("/register")} className="mt-7 bg-sp-orange hover:bg-sp-orangedark rounded-full h-11 px-6">Create a Project</Button>
        </div>
      </section>
    </MarketingLayout>
  );
}
